import { type FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { Select, type SelectOptionType } from "@onshopfront/components";
import LoadingScreen from "../../../app/Common/Loading/LoadingScreen";
import FontAwesomeLight from "../../../app/Utilities/Platform/FontAwesome/FontAwesomeLight";
import { showDialog } from "../../Actions/DialogActions";
import Button from "../../Common/Button";
import CopyButton from "../../Common/CopyButton/CopyButton";
import Input from "../../Common/Input/Input";
import { useTitle } from "../../Utilities/TitleHook";
import { ApplicationSecret } from "./ApplicationSecret";

const LABEL_CLASS = "font-bold text-[var(--color-text-tertiary)] uppercase";

// Secrets are hashed at rest, so every read after issue returns only the prefix
const MASKED_SECRET = `sfcs_${"*".repeat(12)}`;

enum NotifyBy {
    EMAIL = "email",
    SLACK = "slack",
    NONE = "none",
}

const NotifyByOptions = [
    {
        label: "Email",
        value: NotifyBy.EMAIL,
    },
    {
        label: "Slack",
        value: NotifyBy.SLACK,
    },
    {
        label: "Don't notify me",
        value: NotifyBy.NONE,
    },
];

export const ApplicationEdit: FC = () => {
    useTitle("Application Modify");

    const [ name, setName ] = useState("");
    const [ redirectUri, setRedirectUri ] = useState("");
    const [ embedded, setEmbedded ] = useState("");
    const [ setupUri, setSetupUri ] = useState("");
    const [ clientSecret, setClientSecret ] = useState<string>("");
    const [ signingKey, setSigningKey ] = useState<string>("");
    const [ nextSigningKey, setNextSigningKey ] = useState<string>("");
    const [ oldSecret, setOldSecret ] = useState<string | null>(null);
    const [ oldSecretExpiry, setOldSecretExpiry ] = useState<string | null>(null);
    const [ notifyBy, setNotifyBy ] = useState(NotifyBy.NONE);
    const [ notificationConfig, setNotificationConfig ] = useState("");
    const [ error, setError ] = useState<string | null>(null);
    const [ saving, setSaving ] = useState(false);
    const [ loading, setLoading ] = useState(false);
    const [ success, setSuccess ] = useState<string | null>(null);
    const dispatch = useDispatch();
    const [ approved, setApproved ] = useState(false);
    const [ canEmbed, setCanEmbed ] = useState(false);
    const [ marketplace, setMarketplace ] = useState({
        logo            : null,
        homepage        : null,
        support         : null,
        privacy         : null,
        shortDescription: null,
        longDescription : null,
    });

    const showEmbeddedFields = embedded !== "" || canEmbed;

    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);

        fetch(`/application/${id}`, {
            method : "GET",
            headers: {
                Accept        : "application/json",
                "Content-Type": "application/json",
            },
        })
            .then(async response => {
                if(!response.ok) {
                    throw await response.json();
                }

                return response.json();
            })
            .then(body => {
                const client = body.client;
                const notification = body.notification;

                setName(client.name);
                setRedirectUri(client.redirect);
                setEmbedded(client.embedded);
                setClientSecret(client.secret);
                setSigningKey(client.embedded_signing_key ?? "");
                setNextSigningKey(client.next_embedded_signing_key ?? "");
                setOldSecret(client.old_secret);
                setOldSecretExpiry(client.old_secret_expires_at);
                setApproved(!!client.approved);
                setCanEmbed(body.canEmbed);
                setSetupUri(client.setup_url ?? "");
                setMarketplace({
                    logo            : client.logo,
                    homepage        : client.homepage_url,
                    support         : client.support_url,
                    privacy         : client.privacy_url,
                    shortDescription: client.short_description,
                    longDescription : client.long_description,
                });

                if(notification) {
                    setNotifyBy(notification.method);
                    setNotificationConfig(notification.config);
                }
            })
            .catch((err) => {
                console.error("Failed to retrieve application details", err);
                setError(`Unable to retrieve application details: ${err.message || "An unknown error occurred"}`);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [ id ]);

    const resetSecret = () => {
        if(oldSecretExpiry) {
            setError("Client Secret cannot be reset until the old secret has expired");

            return;
        }

        fetch(`/application/${id}/reset-secret`, {
            method     : "POST",
            credentials: "same-origin",
            headers    : new Headers({
                "Accept"      : "application/json",
                "Content-Type": "application/json",
            }),
        })
            .then((response) => {
                if(response.status !== 200) {
                    throw new Error("Invalid Response");
                }

                return response.json();
            })
            .then(result => {
                setOldSecret(result.old_secret);
                setOldSecretExpiry(result.old_secret_expires_at);
                setSuccess("Successfully Reset Client Secret");

                // The plain secret only exists in this response, so surface it before it is replaced
                // by the masked form the server returns from every later read
                dispatch(showDialog(
                    "New Client Secret",
                    <ApplicationSecret clientId={id ?? ""} secret={result.secret} />
                ));

                setTimeout(() => {
                    setSuccess(null);
                }, 5000);
            })
            .catch((err) => {
                console.error("Developer API: Failed to reset Client Secret", err);
                setError(err instanceof Error ? err.message : JSON.stringify(err));
            });
    };

    const signingKeyRotation = (action: "" | "/complete" | "/cancel", message: string) => {
        fetch(`/application/${id}/rotate-signing-key${action}`, {
            method     : "POST",
            credentials: "same-origin",
            headers    : new Headers({
                "Accept"      : "application/json",
                "Content-Type": "application/json",
            }),
        })
            .then(async (response) => {
                const result = await response.json();

                if(response.status !== 200) {
                    throw new Error(result?.message ?? "Invalid Response");
                }

                return result;
            })
            .then(result => {
                setSigningKey(result.signing_key);
                setNextSigningKey(result.next_signing_key ?? "");
                setSuccess(message);

                setTimeout(() => {
                    setSuccess(null);
                }, 5000);
            })
            .catch((err) => {
                console.error("Developer API: Failed to update Embedded Signing Key", err);
                setError(err instanceof Error ? err.message : JSON.stringify(err));
            });
    };

    const clearOldSecret = () => {
        if(!oldSecret) {
            setError("No old secret exists to clear");

            return;
        }

        fetch(`/application/${id}/clear-secret`, {
            method     : "POST",
            credentials: "same-origin",
            headers    : new Headers({
                "Accept"      : "application/json",
                "Content-Type": "application/json",
            }),
        })
            .then((response) => {
                if(response.status !== 204) {
                    throw new Error("Invalid Response");
                }
            })
            .then(() => {
                setOldSecret(null);
                setOldSecretExpiry(null);
                setSuccess("Successfully Cleared Old Secret");

                setTimeout(() => {
                    setSuccess(null);
                }, 5000);
            })
            .catch((err) => {
                console.error("Developer API: Failed to clear old Client Secret", err);
                setError(err instanceof Error ? err.message : JSON.stringify(err));
            });
    };

    const onNotifyChange = (e: SelectOptionType<NotifyBy>): void => {
        if(e && !Array.isArray(e)) {
            setNotifyBy(e.value);
            setNotificationConfig("");
        }
    };

    const onCancel = (): void => {
        navigate("/applications");
    };

    const onConfirm = (): void => {
        if(saving) {
            return;
        }

        setError(null);

        if(!name) {
            setError("Invalid Name");

            return;
        }

        if(!redirectUri) {
            setError("Invalid Redirect URI");

            return;
        }

        setSaving(true);

        fetch(`/application/${id}`, {
            method     : "PUT",
            credentials: "same-origin",
            headers    : {
                "Accept"      : "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                name       : name,
                redirectUri: redirectUri,
                embedded   : embedded,
                setupUri   : setupUri,
                webhook    : notifyBy !== NotifyBy.NONE ? {
                    method: notifyBy,
                    config: notificationConfig,
                } : null,
            }),
        })
            .then(async response => {
                if(!response.ok) {
                    throw await response.json();
                }

                return;
            })
            .then(() => {
                setSuccess("Successfully Updated Application");

                setTimeout(() => {
                    setSuccess(null);
                }, 5000);
            })
            .catch(err => {
                console.error("Could not update the application", err);

                if(typeof err === "object" && err !== null && "message" in err) {
                    setError(err.message);
                } else {
                    setError(err instanceof Error ? err.message : JSON.stringify(err));
                }
            })
            .finally(() => {
                setSaving(false);
            });
    };

    const notifyEmailIconLabel = [ "envelope", "Email Address" ];
    const notifySlackIconLabel = [ "globe", "URL" ];

    const notifySelectValue = NotifyByOptions.find((option) => option.value === notifyBy) ?? null;

    if(loading) {
        return <LoadingScreen />;
    }

    const [
        notifyInputIcon,
        notifyInputLabel,
    ] = notifyBy === NotifyBy.EMAIL ? notifyEmailIconLabel : notifySlackIconLabel;

    return (
        <div className="mt-[50px] p-4 max-w-[theme(screens.lg)] mx-auto">
            {success && (
                <div
                    className={
                        "rounded-lg shadow-md p-4 mb-2 bg-[var(--pill-green-bg)] text-[var(--pill-green-fg)]"
                    }
                >
                    {success}
                </div>
            )}
            {error && (
                <div
                    className={
                        "rounded-lg shadow-md p-4 mb-2 bg-[var(--pill-red-bg)] text-[var(--pill-red-fg)]"
                    }
                >
                    {error}
                </div>
            )}
            <div className="md:flex md:justify-around md:gap-8">
                <div className="flex-1">
                    <Input
                        icon="tag"
                        label="Name"
                        type="text"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.currentTarget.value)}
                    />
                    <Input
                        icon="link"
                        label="Redirect URI"
                        type="text"
                        name="redirectUri"
                        value={redirectUri}
                        onChange={(e) => setRedirectUri(e.currentTarget.value)}
                    />
                    {showEmbeddedFields && (
                        <Input
                            icon="anchor"
                            label="Embedded API URI"
                            type="text"
                            name="embedded"
                            value={embedded}
                            onChange={(e) => setEmbedded(e.currentTarget.value)}
                        />
                    )}
                    {approved && (
                        <Input
                            icon="house"
                            label="Marketplace Direct Authentication URI"
                            type="text"
                            name="setupUri"
                            value={setupUri}
                            onChange={(e) => setSetupUri(e.currentTarget.value)}
                        />
                    )}
                    <Input
                        icon="key"
                        label="Client Secret"
                        type="text"
                        name="secret"
                        value={clientSecret || MASKED_SECRET}
                        disabled={true}
                        hint={
                            "Only shown in full when it's first issued. Reset it below if you no longer " +
                            "have it."
                        }
                    />
                    {showEmbeddedFields && (
                        <div className="flex">
                            <div className="flex-1">
                                <Input
                                    icon="signature"
                                    label="Embedded Signing Key"
                                    type="text"
                                    name="signingKey"
                                    value={signingKey}
                                    disabled={true}
                                    hint={(
                                        <>
                                            Used to verify tokens sent from the Embedded Bridge to your
                                            backend - see{" "}
                                            <Link to="/documentation/Embedded/Authenticating">
                                                Authenticating Requests
                                            </Link>.
                                        </>
                                    )}
                                />
                            </div>
                            <div className="my-auto text-xl">
                                <CopyButton text={signingKey} />
                            </div>
                        </div>
                    )}
                    {showEmbeddedFields && nextSigningKey !== "" && (
                        <div className="flex">
                            <div className="flex-1">
                                <Input
                                    icon="key"
                                    label="Next Embedded Signing Key"
                                    type="text"
                                    name="nextSigningKey"
                                    value={nextSigningKey}
                                    disabled={true}
                                    hint={
                                        "Deploy this, then complete the rotation to start signing with " +
                                        "it. Until you do, we keep signing with the current key."
                                    }
                                />
                            </div>
                            <div className="my-auto text-xl">
                                <CopyButton text={nextSigningKey} />
                            </div>
                        </div>
                    )}
                    {(oldSecret && oldSecretExpiry) && (
                        <>
                            <Input
                                icon="key"
                                label="Old Secret"
                                type="text"
                                name="oldSecret"
                                value={oldSecret || MASKED_SECRET}
                                disabled={true}
                            />
                            <Input
                                icon="clock"
                                label="Old Secret Expires At"
                                type="text"
                                name="oldSecretExpiry"
                                value={oldSecretExpiry}
                                disabled={true}
                            />
                        </>
                    )}
                    <div className="mt-4">
                        <span className={LABEL_CLASS}>Get notified when your webhooks fail</span>
                        <Select
                            options={NotifyByOptions}
                            value={notifySelectValue}
                            onChange={onNotifyChange}
                            className="w-full"
                        />
                        {notifyBy !== NotifyBy.NONE && (
                            <Input
                                icon={notifyInputIcon as IconProp}
                                label={notifyInputLabel}
                                type="text"
                                name="notifyAt"
                                value={notificationConfig}
                                onChange={(e) => setNotificationConfig(e.currentTarget.value)}
                            />
                        )}
                        {notifyBy === NotifyBy.SLACK && (
                            <div
                                className={
                                    "mt-2 p-1 text-center rounded bg-[var(--pill-amber-bg)] " +
                                    "text-[var(--pill-amber-fg)]"
                                }
                            >
                                <p>
                                    To receive Slack notifications, you&apos;ll need to install the{" "}
                                    <a href="https://slack.com/apps/A0F7XDUAZ-incoming-webhooks">
                                        Incoming Webhooks
                                    </a>
                                    {" "}Slack app
                                </p>
                                <p>Once installed and configured, enter the assigned URL above</p>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex-1">
                    {approved && (
                        <>
                            <div className="my-4 text-[var(--pill-green-fg)] text-center">
                                <FontAwesomeLight icon="check-circle" /> Approved
                            </div>
                            <div className="prose mb-4">
                                <p>
                                    This application has been approved by Shopfront. You&apos;re able to integrate
                                    your application with any store on any plan.
                                </p>
                                <p>
                                    You also have priority developer support. If you need assistance, feel free to
                                    email us on{" "}
                                    <a href="mailto:developers@shopfront.com.au">developers@shopfront.com.au</a>
                                </p>
                                <p>
                                    If you&apos;d like to adjust any of the below details, please get in touch with
                                    us using the above email address.
                                </p>
                            </div>
                            {marketplace.logo !== null && (
                                <div className="mb-4">
                                    <span className={LABEL_CLASS}>Logo</span>
                                    <div>
                                        <img
                                            src={marketplace.logo}
                                            alt={`${name} Logo`}
                                            className="max-w-full h-auto md:max-w-sm lg:max-w-lg"
                                        />
                                    </div>
                                </div>
                            )}
                            {marketplace.shortDescription !== null && (
                                <div className="mb-4">
                                    <span className={LABEL_CLASS}>Short Description</span>
                                    <div>
                                        <p>{marketplace.shortDescription}</p>
                                    </div>
                                </div>
                            )}
                            {marketplace.longDescription !== null && (
                                <div className="mb-4">
                                    <span className={LABEL_CLASS}>Long Description</span>
                                    <div className="whitespace-pre-wrap">
                                        {marketplace.longDescription}
                                    </div>
                                </div>
                            )}
                            {marketplace.homepage !== null && (
                                <div className="mb-4">
                                    <span className={LABEL_CLASS}>Homepage</span>
                                    <div>{marketplace.homepage}</div>
                                </div>
                            )}
                            {marketplace.privacy !== null && (
                                <div className="mb-4">
                                    <span className={LABEL_CLASS}>Privacy Policy</span>
                                    <div>{marketplace.privacy}</div>
                                </div>
                            )}
                            {marketplace.support !== null && (
                                <div className="mb-4">
                                    <span className={LABEL_CLASS}>Support</span>
                                    <div>{marketplace.support}</div>
                                </div>
                            )}
                            {setupUri !== "" && (
                                <div className="mb-4">
                                    <span className={LABEL_CLASS}>Setup</span>
                                    <div>{setupUri}</div>
                                </div>
                            )}
                        </>
                    )}
                    {!approved && (
                        <>
                            <div className="my-4 text-[var(--pill-red-fg)] text-center">
                                <FontAwesomeLight icon="times-circle" /> Not Approved
                            </div>
                            <div className="prose">
                                <p>
                                    This application hasn&apos;t been approved by Shopfront. You&apos;re still able
                                    to integrate your application with stores that are running on the Premium plan.
                                </p>
                                <p>
                                    If you&apos;re interested in finding out more about the partner program,{" "}
                                    <Link to="/partner">click here</Link>.
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className="dialog-buttons">
                <Button
                    type="button"
                    classes={[ "normal", "mr-auto!" ]}
                    onClick={oldSecret ? clearOldSecret : resetSecret}
                >
                    {oldSecret ? "Clear Old Secret" : "Reset Secret"}
                </Button>
                {showEmbeddedFields && nextSigningKey === "" && (
                    <Button
                        type="button"
                        classes={[ "normal" ]}
                        onClick={() => signingKeyRotation("", "Successfully Issued New Signing Key")}
                    >
                        Rotate Signing Key
                    </Button>
                )}
                {showEmbeddedFields && nextSigningKey !== "" && (
                    <>
                        <Button
                            type="button"
                            classes={[ "normal" ]}
                            onClick={() => signingKeyRotation("/complete", "Signing Key Rotation Complete")}
                        >
                            Complete Rotation
                        </Button>
                        <Button
                            type="button"
                            classes={[ "normal" ]}
                            onClick={() => signingKeyRotation("/cancel", "Signing Key Rotation Cancelled")}
                        >
                            Cancel Rotation
                        </Button>
                    </>
                )}
                <Button
                    type="button"
                    classes={[ "secondary" ]}
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    classes={[ "primary" ]}
                    onClick={onConfirm}
                    disabled={saving}
                >
                    {saving ? "Saving..." : "Confirm"}
                </Button>
            </div>
        </div>
    );
};
